import { useMemo } from "react";
import { motion } from "framer-motion";

const IVORY = "#f4efe8";
const COPPER = "#b88457";
const EASE = [0.22, 1, 0.36, 1];

// Cap-height reference = 100 units. Baseline at y = 150.
const BASE = 150;
const CAP = 100;

// Per-letter nominal widths + optical gaps (hand-tuned kerning)
const LETTERS = [
  { ch: "A", w: 80, special: "A" },
  { ch: "S", w: 60 },
  { ch: "R", w: 70, special: "R" },
  { ch: "I", w: 26 },
  { ch: "T", w: 64 },
  { ch: "A", w: 80, special: "A" },
  { ch: "S", w: 60 },
];
// gap that follows each letter (except last)
const GAPS = [22, 34, 32, 32, 30, 22];

function layout() {
  let cursor = 0;
  const items = LETTERS.map((l, i) => {
    const cx = cursor + l.w / 2;
    cursor += l.w + (GAPS[i] || 0);
    return { ...l, cx };
  });
  const wordW = cursor;
  const dotR = 12;
  const dotCx = wordW + 30 + dotR;
  const totalW = dotCx + dotR + 8;
  return { items, wordW, dotCx, dotR, totalW };
}

export default function AsritasWordmark({ height = 40, showDescriptor = false, className = "", descriptor = "INVESTIGATION INTELLIGENCE" }) {
  const { items, wordW, dotCx, dotR, totalW } = useMemo(layout, []);

  const top = showDescriptor ? 0 : 44;
  const vbH = 172 - top;
  const width = height * (totalW / vbH);

  return (
    <motion.svg
      viewBox={`0 ${top} ${totalW} ${vbH}`}
      width={width}
      height={height}
      className={className}
      style={{ overflow: "visible" }}
      initial="hidden"
      animate="show"
    >
      {/* Descriptor — quiet, wide-tracked, uppercase serif (editorial, not technical) */}
      {showDescriptor && (
        <motion.text
          x={wordW / 2}
          y={28}
          textAnchor="middle"
          fill={COPPER}
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, letterSpacing: 9, fontWeight: 500 }}
          variants={{ hidden: { opacity: 0 }, show: { opacity: 0.85 } }}
          transition={{ duration: 1, ease: EASE, delay: 0.9 }}
        >
          {descriptor}
        </motion.text>
      )}

      {/* Wordmark letters */}
      <motion.g variants={{ show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } } }}>
        {items.map((l, i) => (
          <motion.text
            key={i}
            x={l.cx}
            y={BASE}
            textAnchor="middle"
            fill={IVORY}
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: CAP, fontWeight: 500 }}
            variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.75, ease: EASE }}
          >
            {l.ch}
          </motion.text>
        ))}
      </motion.g>

      {/* Custom-A accent: upward chevron (arrow) in the counter — discovery / root */}
      {items
        .filter((l) => l.special === "A")
        .map((l, i) => (
          <motion.path
            key={`a${i}`}
            d={`M${l.cx - 11},131 L${l.cx},103 L${l.cx + 11},131`}
            fill="none"
            stroke={COPPER}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            variants={{ hidden: { pathLength: 0, opacity: 0 }, show: { pathLength: 1, opacity: 0.92 } }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 + i * 0.1 }}
          />
        ))}

      {/* Custom-R accent: subtly branching leg — causal tree */}
      {items
        .filter((l) => l.special === "R")
        .map((l, i) => {
          const x = l.cx;
          return (
            <motion.g
              key={`r${i}`}
              variants={{ hidden: { pathLength: 0, opacity: 0 }, show: { pathLength: 1, opacity: 0.85 } }}
              transition={{ duration: 0.7, ease: EASE, delay: 1 }}
            >
              <motion.path d={`M${x + 12},128 L${x + 26},150`} stroke={COPPER} strokeWidth="2" strokeLinecap="round" fill="none" />
              <motion.path d={`M${x + 20},139 L${x + 32},134`} stroke={COPPER} strokeWidth="1.4" strokeLinecap="round" fill="none" />
              <motion.path d={`M${x + 23},144 L${x + 34},150`} stroke={COPPER} strokeWidth="1.4" strokeLinecap="round" fill="none" />
            </motion.g>
          );
        })}

      {/* Origin dot — copper, ~22% cap height. The origin, not punctuation. */}
      <motion.circle
        cx={dotCx}
        cy={134}
        r={dotR}
        fill={COPPER}
        style={{ transformOrigin: `${dotCx}px 134px` }}
        variants={{ hidden: { scale: 0, opacity: 0 }, show: { scale: 1, opacity: 1 } }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
      />
    </motion.svg>
  );
}
